/**
 * Vario bettet das Frontend in ein iframe mit Initialhoehe 0 ein. Die Hoehe muss deshalb
 * aktiv beim Host angefordert werden, sonst sieht der Nutzer ein leeres Panel.
 *
 * Dafuer gibt es zwei Verfahren:
 *   - `requestVarioFullHeight()` — volle verfuegbare Viewport-Hoehe anfordern. Der Weg fuer
 *     alle Fenster-Slots (Vario-Hinweis 2026-08-01, siehe Funktions-Doc unten).
 *   - `initVarioIframeHeight('auto')` — eigene Content-Hoehe melden. Nur noch fuer Varios
 *     Installations-Dialoge, die mitwachsen sollen statt die Flaeche zu fuellen.
 *
 * Transport spiegelt @vario-software/vario-app-framework-frontend/communication.js:
 * `{ appIdentifier, integrationId, ...payload }` an window.parent. Das Framework-Paket wird
 * bewusst nicht importiert — dieses Frontend hat einen eigenen Bootstrap (main.ts +
 * vario-handshake.ts), und `sendMain` dort ist funktionsgleich zu `sendVarioMessage`.
 */
function readUrlParam(name: string): string | undefined {
  return new URL(window.location.href).searchParams.get(name) ?? undefined;
}

export const appIdentifier = readUrlParam('appIdentifier');
export const integrationId = readUrlParam('integrationId');

export function sendVarioMessage(payload: Record<string, unknown>) {
  try {
    window.parent.postMessage({ appIdentifier, integrationId, ...payload }, '*');
  } catch {
    // ignore — top-level open
  }
}

/**
 * VARIO-Ready-Signal (App-Framework-Update, Host-Auswertung ab 2026-08-20):
 * Meldet dem Host `{ appIdentifier, integrationId, ready: true }` = App vollständig
 * geladen und kommunikationsbereit. Erst danach gilt die App als erreichbar
 * (Install/Uninstall werden nur dann ausgeführt), sonst Ladeanimation/Fehler.
 * Nach erfolgreichem Bootstrap EINMAL aufrufen.
 */
export function signalVarioReady() {
  sendVarioMessage({ ready: true });
}

let lastReported = -1;

/**
 * Iframe-Hoehe je nach Slot-Kontext:
 *   - "fixed": konstanter Korridor. Seit der useFullHeight-Umstellung (2026-08-01) nur noch
 *     der Rueckfallpfad des Watchdogs, kein regulaerer Aufrufer mehr.
 *   - "auto":  ResizeObserver auf body, postet die aktuelle scrollHeight (mit
 *     small min-cap). Regulaer nur noch fuer install/uninstall.
 */
// Rueckfallhoehe der Fenster-Slots, falls der Host useFullHeight nicht auswertet.
const FIXED_HEIGHT = 900;
// Rueckfallhoehe des listing-status-Slots (Vario-Artikel → Verkaufskanaele), falls der Host
// useFullHeight nicht auswertet. Kalibriert auf den sichtbaren Vario-Artikel-Bereich: zu gross
// → unterer Teil ragt aus dem Panel; zu klein → unnoetig viel Scroll. Im Normalfall ungenutzt,
// weil Vario die verfuegbare Hoehe selbst setzt.
export const LISTING_STATUS_HEIGHT = 620;
// Defaults des Auto-Modus. Seit dem Wegfall des masterdata-Slots (2026-07-28) gibt es keinen
// Aufrufer mehr, der sie ungesetzt laesst — install/uninstall uebergeben 360/1200. Sie bleiben
// als konservativer Fallback fuer kuenftige Auto-Slots stehen.
const AUTO_MIN_HEIGHT = 540;
const AUTO_MAX_HEIGHT = 900;

function postHeight(height: number) {
  if (height === lastReported) return;
  lastReported = height;
  sendVarioMessage({ height });
}

export type IframeHeightMode = 'fixed' | 'auto';

export function initVarioIframeHeight(
  mode: IframeHeightMode = 'fixed',
  fixedHeight: number = FIXED_HEIGHT,
  // Auto-Mode: Untergrenze der gemeldeten Inhaltshoehe. Kompakte Dialoge (install/uninstall)
  // uebergeben 360, sonst blaeht der 540er-Default die kleine Ergebnis-Karte zu einem
  // ueberdimensionierten Fenster auf.
  autoMinHeight: number = AUTO_MIN_HEIGHT,
  // Auto-Mode: Obergrenze. Default AUTO_MAX_HEIGHT (900). Wichtig: Vario laesst im iframe
  // KEINEN eigenen Scrollbalken zu (der Container clippt) — ein zu knapper Deckel macht den
  // unteren Teil unerreichbar. Slots, deren Inhalt wachsen kann, geben mehr Luft.
  autoMaxHeight: number = AUTO_MAX_HEIGHT,
) {
  if (mode === 'fixed') {
    postHeight(fixedHeight);
    window.addEventListener('resize', () => postHeight(fixedHeight));
    return;
  }

  // Auto-Mode: aktuelle Inhaltshoehe (Body-scrollHeight) tracken und posten.
  // Wir clampen auf [autoMinHeight, autoMaxHeight], damit weder kollabierte Anfangs-
  // Renders (Hoehe 0) noch versehentlich endlos-wachsende Inhalte das Vario-
  // iframe sprengen. Updates sind throttled per requestAnimationFrame —
  // mehrere Mutations im selben Frame fuehren nur zu einem postMessage.
  let rafScheduled = false;
  const measureAndPost = () => {
    rafScheduled = false;
    const raw = document.body?.scrollHeight ?? autoMinHeight;
    const clamped = Math.max(autoMinHeight, Math.min(autoMaxHeight, raw));
    postHeight(clamped);
  };
  const schedule = () => {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(measureAndPost);
  };
  schedule();
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(schedule).observe(document.body);
  }
  // Backup-Listener fuer dyn. Content-Inserts ohne layout-Shift, die der
  // ResizeObserver allein nicht zuverlaessig sieht.
  const mo = new MutationObserver(schedule);
  mo.observe(document.body, { childList: true, subtree: true, attributes: true });
  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);
}

/**
 * Fordert beim Vario-Host die volle verfuegbare Viewport-Hoehe an (Vario-Hinweis 2026-08-01).
 *
 * Vorher meldete die App ihre Content-Hoehe und Vario setzte das iframe exakt darauf — das war
 * die Ursache des Render-Springens, weil die Hoehe staendig nachgezogen wurde. Nach dieser
 * Message uebernimmt Vario das Hoehen-Handling komplett, inklusive Neuberechnung bei
 * Sidebar-Toggle und Fenstergroesse. Einmal senden genuegt, kein Nachmessen.
 *
 * `sendVarioMessage` haengt appIdentifier + integrationId an; fehlen sie, ignoriert der Host
 * die Message stillschweigend (dafuer der Watchdog unten).
 *
 * App-seitige Voraussetzung — bereits erfuellt, nicht entfernen: Layout auf 100 % Hoehe mit
 * INTERNEM Scrolling. Fenster-Slots ueber bridge.css §16 (html/body/main#app auf 100 %,
 * .kl-tab-panel scrollt), listing-status ueber .kls-shell (height:100vh, overflow-y:auto).
 * Nicht fuer install/uninstall: dort schoebe die volle Hoehe Varios eigenen Dialog-Button
 * weit unter die Karte.
 */
export function requestVarioFullHeight() {
  sendVarioMessage({ useFullHeight: true });
}

// Watchdog: `useFullHeight` ist eine rein host-seitige Message — weder der Framework-Snapshot
// (docs/vario-app-framework) noch das Developer-Portal kennen sie, wir stuetzen uns auf Varios
// Zusage. Wertet ein Host sie nicht aus und wir posten keine Hoehe mehr, bliebe das iframe auf
// Initialhoehe 0 stehen: ein LEERES Fenster. Die iframe-Hoehe ist aus App-Sicht die
// Viewport-Hoehe, `window.innerHeight` ist also das direkte Signal, ob der Host uebernommen hat.
// 200px liegt weit unter jeder realistischen Fensterhoehe und weit ueber 0, trifft also nur den
// echten Fehlerfall.
//
// 2000ms ist der in Otto erprobte Wert, bewusst grosszuegig: Ein zu frueher Rueckfall waere das
// schlechtere Risiko, weil wir dann eine feste Hoehe GEGEN Varios gerade anlaufendes Handling
// posten. Die zusaetzliche Wartezeit kostet nur im echten Fehlerfall etwas.
//
// Der Fehlerfall ist nicht theoretisch: Bei Gambios Inline-Detail-Slots hat Vario useFullHeight
// ignoriert, worauf der iframe auf 0 kollabierte („Feld da, aber leer"). Genau deshalb faehrt
// auch listing-status hier mit Netz.
const FULL_HEIGHT_PROBE_MS = 2000;
const FULL_HEIGHT_MIN_VIEWPORT = 200;

/**
 * Wie `requestVarioFullHeight`, aber mit Rueckfall auf den alten Festhoehen-Pfad, falls der
 * Host nicht reagiert. Der Rueckfall wird geloggt statt still zu passieren.
 */
export function requestVarioFullHeightWithFallback(fallbackHeight: number = FIXED_HEIGHT) {
  requestVarioFullHeight();
  window.setTimeout(() => {
    if (window.innerHeight >= FULL_HEIGHT_MIN_VIEWPORT) return;
    console.warn(
      `[kaufland-app] useFullHeight ohne Wirkung (Viewport ${window.innerHeight}px) — ` +
      `Rueckfall auf feste Hoehe ${fallbackHeight}px`,
    );
    initVarioIframeHeight('fixed', fallbackHeight);
  }, FULL_HEIGHT_PROBE_MS);
}
